import { useState } from "preact/hooks";
import { Byline } from "../chrome/byline.js";
import { GuestNav } from "../chrome/guest-nav.js";
import { HostMark } from "../chrome/host-mark.js";
import { dayLabel } from "../clock.js";
import { byHour, type ViewPhoto } from "../photos.js";
import { COVER_ID, seedSrc } from "../seed.js";
import type { DemoBrand } from "../types.js";
import { Viewer } from "./viewer.js";

export interface AlbumScreenProps {
  readonly brand: DemoBrand;
  readonly logoUrl: string | null;
  readonly accent: string;
  readonly photos: readonly ViewPhoto[];
  readonly today: Date;
  readonly client?: boolean;
}

export function AlbumScreen({
  brand,
  logoUrl,
  accent,
  photos,
  today,
  client = false,
}: AlbumScreenProps) {
  const [hour, setHour] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const groups = byHour(photos);
  const shown = hour === null ? groups : groups.filter((group) => group.hour === hour);
  const flat = shown.flatMap((group) => group.photos);
  return (
    <div
      class={client ? "d-screen d-scroll d-guest-page is-client" : "d-screen d-scroll d-guest-page"}
      data-theme="dark"
      data-testid="screen-album"
    >
      <header class="d-album-head">
        <div class="d-cover">
          <img src={seedSrc(COVER_ID, "l")} alt="" />
          <span class="d-veil-soft" />
          <span class="d-veil-bottom" />
        </div>
        <div class="d-album-head-body">
          <HostMark name={brand.name} logoUrl={logoUrl} />
          <span class="d-accent-rule" style={{ background: accent }} />
          <h1 class="d-album-title">{brand.eventName}</h1>
          <p class="d-album-line">
            {dayLabel(today)} · 19h00 · {photos.length} fotos
          </p>
        </div>
      </header>
      {client ? (
        <div class="d-album-deliver" data-theme="light">
          <div class="d-album-deliver-row">
            <span class="d-badge">Qualidade original</span>
            <button
              type="button"
              class="d-button d-primary d-small"
              aria-expanded={saving}
              onClick={() => setSaving((value) => !value)}
            >
              Baixar o álbum
            </button>
          </div>
          {saving ? (
            <p class="d-caption" data-testid="album-download-note">
              No evento real, as fotos saem em qualidade original para baixar, sem a compressão do
              WhatsApp. Nas faixas pagas, o álbum fica no ar por 12 meses.
            </p>
          ) : null}
        </div>
      ) : null}
      <div class="d-chips" role="group" aria-label="Horas">
        <button
          type="button"
          class="d-chip"
          aria-pressed={hour === null}
          onClick={() => setHour(null)}
        >
          tudo <span class="d-chip-count">{photos.length}</span>
        </button>
        {groups.map((group) => (
          <button
            key={group.hour}
            type="button"
            class="d-chip"
            aria-pressed={hour === group.hour}
            onClick={() => setHour(group.hour)}
          >
            {group.hour}h
          </button>
        ))}
      </div>
      <div class="d-album-body">
        {shown.map((group) => (
          <section key={group.hour} class="d-hour">
            <h3 class="d-section-title">
              <span>{group.hour}h</span>
              <span>{group.photos.length}</span>
            </h3>
            <div class="d-grid3">
              {group.photos.map((photo) => (
                <button
                  key={photo.id}
                  type="button"
                  class="d-tile-button"
                  aria-label={`Foto de ${photo.author}`}
                  onClick={() => setOpen(flat.indexOf(photo))}
                >
                  <img class="d-tile" src={photo.small} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
      <Byline />
      {client ? null : <GuestNav active="album" />}
      {open !== null ? (
        <Viewer photos={flat} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}
